/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IActionContext } from '@msdyn365-commerce/core-internal';
import { ExtensibleEnumeration, StoreOperationsDataActions } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import RenderOrderLookupAdditionalFieldInput from './components/order-lookup-additional-fields';
import RenderOrderLookupFormFields from './components/order-lookup-fields';
import {
    IOrderLookupProps,
    IOrderLookupSearchAdditionalFieldsData,
    orderStatusLookUpParametersType as orderStatusLookUpParametersTypes } from './order-lookup.props.autogenerated';
import {
    isAdditionalFieldValid,
    isEmailValid,
    renderErrorLabel,
    renderFieldErrors,
    renderRegisteredUserMessage,
    validateOrderLookupAdditionalField,
    validateOrderLookupField
} from './utilities/order-lookup-utility';

/**
 * OrderLookup view props.
 */
export interface IOrderLookupViewProps extends IOrderLookupProps<{}> {
    additionalProperties: JSX.Element[] | undefined;
    orderLookup: IModuleProps;
    orderLookupContainer: INodeProps;
    orderLookupAdditionalFieldContainer: INodeProps;
    paragraph?: React.ReactNode;
    heading?: React.ReactNode;
    submitButton?: INodeProps;
    submitButtonLabelText?: string;
    orderLookupForm?: INodeProps;
    renderOrderLookupInputField: JSX.Element | undefined;
    orderNumberInput?: JSX.Element | undefined;
    errorMessage?: React.ReactNode;
    fieldErrorMessage?: React.ReactNode;
    registeredUserMessage?: React.ReactNode;
    onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
    submit?(): void;
}

/**
 * OrderLookupSearchValues --  supported values.
 */
enum OrderLookupSearchValues {
    email = 'email',
    ordernumber = 'ordernumber'
}

/**
 * To render and handle state for additional order lookup fields.
 */
export interface IOrderLookupAdditionalFields {
    labelName: string;
    isValidFieldName: boolean;
    fieldValue: string;
    ariaLabel: string;
    additionalfieldErrorMessage: string;
}

/**
 * OrderLookup view props.
 */
export interface IOrderLookupFormState {
    orderNumber: string;
    email: string;
    isOrdNumberError: boolean;
    isEmailError: boolean;
    isValidEmailError: boolean;
    isReqError: boolean;
    isDisabled: boolean;
    isValidLookupField: boolean;
    isValidAdditionalLookupField: boolean;
    responseErrorMessage: string;
    additionalProperties: IOrderLookupAdditionalFields[];
}

/**
 *
 * OrderLookup component.
 * @extends {React.PureComponent<IOrderLookupProps<{}>>}
 */
class OrderLookup extends React.PureComponent<IOrderLookupProps<{}>, IOrderLookupFormState> {
    public orderNumberInputRef: React.RefObject<HTMLInputElement>;

    public emailInputRef: React.RefObject<HTMLInputElement>;

    public isEmailValue: boolean = false;

    private requiredErrorMessage: string | undefined;

    private emailValidationErrorMessage: string | undefined;

    private isOrderNumberValue: boolean = false;

    private orderLookupExtensibleEnum: ExtensibleEnumeration[] = [];

    public constructor(props: IOrderLookupProps<{}>) {
        super(props);
        this.orderNumberInputRef = React.createRef();
        this.emailInputRef = React.createRef();
        const { config } = this.props;
        const { orderLookupSearchAdditionalFields } = config;
        const additionalPropertyStates: IOrderLookupAdditionalFields[] = [];

        if (orderLookupSearchAdditionalFields) {
            for (const item of orderLookupSearchAdditionalFields) {
                additionalPropertyStates.push({
                    labelName: item.labelName ?? '',
                    isValidFieldName: true,
                    fieldValue: '',
                    ariaLabel: item.ariaLabel ?? '',
                    additionalfieldErrorMessage: item.validationMessage ?? ''
                });
            }
        }

        this.state = {
            orderNumber: '',
            email: '',
            isOrdNumberError: false,
            isEmailError: false,
            isValidEmailError: false,
            isReqError: false,
            isDisabled: true,
            isValidLookupField: true,
            isValidAdditionalLookupField: false,
            responseErrorMessage: '',
            additionalProperties: additionalPropertyStates
        };
    }

    public render(): JSX.Element | null {
        const {
            config: {
                paragraph,
                heading
            },
            resources,
            context
        } = this.props;

        const {
            orderLookupDefaultHeadingText,
            orderLookupButtonLabel,
            orderLookupFormAriaLabel,
            orderLookupOrderNumberLabel,
            orderLookupOrderNumberAriaLabel,
            orderLookupSubmitAriaLabel,
            orderLookupFieldValidationErrorMessage
        } = resources;

        const {
            responseErrorMessage,
            isDisabled,
            orderNumber,
            isOrdNumberError
        } = this.state;

        const headingText = StringExtensions.isNullOrEmpty(heading?.text) ? orderLookupDefaultHeadingText : heading?.text;
        const headingTag = heading?.tag ?? 'h1';

        const moduleClassName = 'ms-order-lookup';
        const orderLookupContainerClassName = `${moduleClassName}__details`;
        const orderLookupHeadingClassName = `${orderLookupContainerClassName}__heading`;
        const orderLookupFormClassName = `${orderLookupContainerClassName}__form`;
        const orderLookupAdditionalFiledContainerClassName = `${orderLookupFormClassName}__additional-fields-container`;
        const orderLookupFormFieldsClassName = `${orderLookupFormClassName}__fields`;
        const orderNumberLabelClassName = `${orderLookupFormFieldsClassName}__order-number-label`;
        const orderNumberInputErrorClassName = `${orderLookupFormFieldsClassName}__order-number-input-error`;
        const submitButtonClassName = `${orderLookupFormClassName}__submit`;
        const labelErrorMessageClassName = `${moduleClassName}__error-message`;
        const registeredUserClassName = `${moduleClassName}__registered-user`;
        const signinLinkClassName = `${registeredUserClassName}__sign-in-link msc-btn`;

        const orderLookupHeading = (
            <Msdyn365.Text
                className={orderLookupHeadingClassName}
                tag={headingTag}
                text={headingText}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );

        const viewProps: IOrderLookupViewProps = {
            ...this.props,
            orderLookup: {
                moduleProps: this.props,
                className: moduleClassName
            },

            orderLookupContainer: {
                className: orderLookupContainerClassName
            },
            orderLookupAdditionalFieldContainer: {
                className: orderLookupAdditionalFiledContainerClassName
            },
            paragraph: paragraph && this._createParagraph(paragraph),
            heading: orderLookupHeading,

            renderOrderLookupInputField: this._renderOrderLookupInput(),
            orderLookupForm: {
                className: orderLookupFormClassName,
                tag: 'form',
                'aria-label': orderLookupFormAriaLabel,
                name: 'orderLookupForm',
                role: 'form',
                autoComplete: 'off',
                onSubmit: this.handleSubmit
            },
            orderNumberInput: (
                <RenderOrderLookupFormFields
                    inputReference={this.orderNumberInputRef}
                    ariaLabel={orderLookupOrderNumberAriaLabel}
                    className={orderLookupFormFieldsClassName}
                    inputType='text'
                    value={orderNumber}
                    name={OrderLookupSearchValues.ordernumber}
                    orderLookupLabel={orderLookupOrderNumberLabel}
                    orderLookupLabelClassName={orderNumberLabelClassName}
                    fieldErrorClassName={orderNumberInputErrorClassName}
                    isInputTypeError={false}
                    isValidInputField={isOrdNumberError}
                    renderAlert={this._renderOrderNumberAlert}
                    onChange={this.onChange}
                />
            ),

            submitButton: {
                className: submitButtonClassName,
                tag: Button,
                'aria-label': orderLookupSubmitAriaLabel,
                type: 'submit',
                role: 'button',
                title: orderLookupButtonLabel,
                onClick: this.handleSubmit,
                disabled: isDisabled
            },

            submitButtonLabelText: orderLookupButtonLabel,
            errorMessage: renderErrorLabel(responseErrorMessage, labelErrorMessageClassName),
            fieldErrorMessage: (!this.state.isValidLookupField || this.state.isValidAdditionalLookupField) &&
            renderFieldErrors(orderLookupFieldValidationErrorMessage),
            registeredUserMessage: renderRegisteredUserMessage(resources, context.request.user.signInUrl ?? '', signinLinkClassName, registeredUserClassName),
            onChange: this.onChange,
            submit: this.handleSubmit,
            additionalProperties: this._renderAdditionalFields()
        };
        return this.props.renderView(viewProps);
    }

    public async componentDidMount(): Promise<void> {
        const { config, context } = this.props;
        const { actionContext } = context;
        const { orderStatusLookUpParametersType, orderLookupSearchAdditionalFields } = config;
        let isOrderLookupAdditionalFieldValid: boolean = false;
        let isOrderLookupFieldValid: boolean = false;

        const getExtensibleEnumerations = await StoreOperationsDataActions.getExtensibleEnumerationsAsync(
            { callerContext: actionContext });
        this.orderLookupExtensibleEnum = getExtensibleEnumerations.find(
            item => item.TypeName === 'OrderLookupAdditionalCriteriaType')?.ExtensibleEnumerations ?? [];

        const orderLookupParameter: string = orderStatusLookUpParametersType ?? '';
        const orderLookupAdditionalParameter: IOrderLookupSearchAdditionalFieldsData[] = orderLookupSearchAdditionalFields ?? [];

        isOrderLookupFieldValid = validateOrderLookupField(orderLookupParameter, this.orderLookupExtensibleEnum);
        isOrderLookupAdditionalFieldValid = validateOrderLookupAdditionalField(orderLookupAdditionalParameter, this.orderLookupExtensibleEnum);
        this.setLookupFieldState(isOrderLookupFieldValid, isOrderLookupAdditionalFieldValid);
    }

    /**
     * Handle text change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleTextChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handle paragraph change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent): void => {
        const { config } = this.props;
        config.paragraph = event.target.value;
    };

    /**
     * Sets isValidLookupField after validating with extensible emun.
     * @param isValidField - Sets the property.
     * @param isValidAdditionalField - Sets the additional field property.
     */
    public setLookupFieldState(isValidField: boolean, isValidAdditionalField: boolean): void {
        this.setState({ isValidLookupField: isValidField, isValidAdditionalLookupField: isValidAdditionalField });
    }

    /**
     * HandleSubmit submit.
     */
    public readonly handleSubmit = (): void => {
        const { context } = this.props;
        const { actionContext } = context;
        const { additionalProperties, orderNumber, email } = this.state;
        const orderLookupValue = email;
        const orderLookupName = OrderLookupSearchValues.email;
        const isValid: boolean = this._validateOrderLookupForm();
        if (isValid) {
            const actionContextValue: IActionContext = actionContext;
            const orderDetailsPath = Msdyn365.getUrlSync('orderDetails', actionContextValue) ?? '';
            if (orderDetailsPath) {
                const baseUrl = new URL(window.location.origin);
                const redirectUrl = new URL(orderDetailsPath, baseUrl);
                const searchParameters = redirectUrl.searchParams;

                searchParameters.set('confirmationId', orderNumber);
                searchParameters.set('propertyName', orderLookupName);
                searchParameters.set('propertyValue', orderLookupValue);

                if (ArrayExtensions.hasElements(additionalProperties)) {
                    additionalProperties.map((item, index) => {
                        const fieldName = !StringExtensions.isNullOrWhitespace(item.ariaLabel) ? item.ariaLabel.replace(' ', '').toLocaleLowerCase() : '';
                        const fieldValue = !StringExtensions.isNullOrWhitespace(item.fieldValue) ? item.fieldValue : '';
                        searchParameters.set(`field${index + 1}Name`, fieldName);
                        searchParameters.set(`field${index + 1}Value`, fieldValue);
                        return item;
                    });
                }

                window.location.assign(redirectUrl.toString());
            }
        }
    };

    /**
     * OnChange event.
     * @param event - Value of input field.
     */
    public readonly onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { additionalProperties } = this.state;
        const { config } = this.props;
        const { orderStatusLookUpParametersType } = config;
        const name = event.target.name || '';
        const value = event.target.value || '';
        this.isOrderNumberValue = !StringExtensions.isNullOrWhitespace(this.orderNumberInputRef.current?.value);
        this.isEmailValue = !StringExtensions.isNullOrWhitespace(this.emailInputRef.current?.value);

        if (orderStatusLookUpParametersType?.toLocaleLowerCase() === 'none') {
            this.isEmailValue = true;
        }

        this.setState({ responseErrorMessage: '' });
        const additionalPropertyStates: IOrderLookupAdditionalFields[] = [];
        let isAdditionalFieldError: boolean = true as boolean;
        let isKeyExistInAdditionalFieldArray: boolean = false as boolean;

        for (const item of additionalProperties) {
            const fieldName: string = item.ariaLabel.replace(' ', '');
            if (fieldName.toLocaleLowerCase() === name.toLocaleLowerCase()) {
                additionalPropertyStates.push({
                    labelName: item.labelName,
                    isValidFieldName: true,
                    fieldValue: value,
                    ariaLabel: item.ariaLabel,
                    additionalfieldErrorMessage: item.additionalfieldErrorMessage
                });
            } else {
                additionalPropertyStates.push({
                    labelName: item.labelName,
                    isValidFieldName: item.isValidFieldName,
                    fieldValue: item.fieldValue,
                    ariaLabel: item.ariaLabel,
                    additionalfieldErrorMessage: item.additionalfieldErrorMessage
                });
            }
            if (fieldName.toLocaleLowerCase() === orderStatusLookUpParametersType?.toLocaleLowerCase()) {
                isKeyExistInAdditionalFieldArray = true;
            }
        }

        for (const items of additionalPropertyStates) {
            if (StringExtensions.isNullOrWhitespace(items.fieldValue)) {
                isAdditionalFieldError = false;
            }
        }

        this.setState({ additionalProperties: additionalPropertyStates });

        if (!isKeyExistInAdditionalFieldArray) {
            switch (name.toLocaleLowerCase()) {
                case OrderLookupSearchValues.ordernumber: {
                    this.setState({
                        orderNumber: value,
                        isOrdNumberError: false,
                        isReqError: false
                    });
                    break;
                }
                case OrderLookupSearchValues.email: {
                    this.setState({
                        email: value,
                        isEmailError: false,
                        isValidEmailError: false,
                        isReqError: false
                    });
                    break;
                }
                default: {
                    this.setState({ isReqError: false });
                }
            }
        }

        if (this.isOrderNumberValue &&
            this.isEmailValue &&
            isAdditionalFieldError &&
            this.state.isValidLookupField &&
            !this.state.isValidAdditionalLookupField
        ) {
            this.setState({ isDisabled: false });
        } else {
            this.setState({ isDisabled: true });
        }
    };

    private _createParagraph(text?: Msdyn365.RichText): React.ReactNode | null {
        const { context } = this.props;
        const { request } = context;
        if (!text) {
            return null;
        }
        return (
            <Msdyn365.RichTextComponent
                text={text}
                className='ms-order-lookup__details__text'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: request }}
            />
        );
    }

    /**
     * Render order lookup validation message.
     * @param className - Class name for error field.
     * @returns - JSX.Element.
     */
    private readonly _renderAlert = (className: string): JSX.Element => {
        const {
            resources: {
                orderLookupEmailValidationErrorMessage,
                orderLookupRequiredTextErrorMessage
            }
        } = this.props;

        const {
            isEmailError,
            email,
            isValidEmailError,
            isReqError
        } = this.state;
        if (isEmailError) {
            this.requiredErrorMessage = orderLookupRequiredTextErrorMessage;
        }

        if (!StringExtensions.isNullOrEmpty(email) && isValidEmailError) {
            this.emailValidationErrorMessage = orderLookupEmailValidationErrorMessage;
        }

        const isRequireError = isReqError;
        const isEmailValidError = isValidEmailError;
        const isValidEmailInput = className.toLocaleLowerCase() === OrderLookupSearchValues.email;
        return (
            <>
                {isRequireError && (
                    <span className={`${className}__alert`} role='alert'>
                        {this.requiredErrorMessage}
                    </span>
                )}
                {isEmailValidError && isValidEmailInput && (
                    <span className={`${className}__alert`} role='alert'>
                        {this.emailValidationErrorMessage}
                    </span>
                )}
            </>
        );
    };

    /**
     * Render order number validation message.
     * @param className - Class name for error field.
     * @returns - JSX.Element.
     */
    private readonly _renderOrderNumberAlert = (className: string): JSX.Element => {
        const {
            resources: {
                orderLookupOrderNumberValidationErrorMessage,
                orderLookupRequiredTextErrorMessage
            }
        } = this.props;

        const {
            isOrdNumberError,
            isReqError
        } = this.state;
        return (
            <>
                {isReqError && (
                    <span className={`${className}__alert`} role='alert'>
                        {orderLookupRequiredTextErrorMessage}
                    </span>
                )}
                {isOrdNumberError && (
                    <span className={`${className}__alert`} role='alert'>
                        {orderLookupOrderNumberValidationErrorMessage}
                    </span>
                )}
            </>
        );
    };

    private _renderAdditionalFields(): JSX.Element[] | undefined {
        const { additionalProperties } = this.state;
        const additionalFieldsInput: JSX.Element[] = [];
        for (const item of additionalProperties) {
            const name: string = item.ariaLabel.replace(' ', '');
            additionalFieldsInput.push(
                <RenderOrderLookupAdditionalFieldInput
                    className={name.toLocaleLowerCase()}
                    ariaLabel={item.ariaLabel}
                    inputType='text'
                    value={item.fieldValue}
                    name={name.toLocaleLowerCase()}
                    additionalFieldLabel={item.labelName}
                    additionalFieldLabelClassName='ms-order-lookup__details__form__additional-fields__label'
                    isValidAdditionalFieldName={item.isValidFieldName}
                    additionalFieldValidationMessage={item.additionalfieldErrorMessage}
                    onChange={this.onChange}
                />
            );
        }
        return additionalFieldsInput;
    }

    /**
     * Render Input field for order lookup.
     * @returns - IOrderLookupFieldInfo.
     */
    private readonly _renderOrderLookupInput = (): JSX.Element | undefined => {
        const { email, isEmailError, isValidEmailError } = this.state;
        const {
            config: {
                orderStatusLookUpParametersType
            },
            resources: {
                orderLookupEmailLabel,
                orderLookupEmailAriaLabel
            }
        } = this.props;

        switch (orderStatusLookUpParametersType) {
            case orderStatusLookUpParametersTypes.none:
                return undefined;
            case orderStatusLookUpParametersTypes.email:
            default:
                return (
                    <RenderOrderLookupFormFields
                        inputReference={this.emailInputRef}
                        ariaLabel={orderLookupEmailAriaLabel}
                        className='ms-order-lookup__details__form__fields'
                        inputType='text'
                        value={email}
                        name={OrderLookupSearchValues.email}
                        orderLookupLabel={orderLookupEmailLabel}
                        orderLookupLabelClassName='ms-order-lookup__details__form__fields__email-label'
                        fieldErrorClassName='ms-order-lookup__details__form__fields__email-input-error'
                        isInputTypeError={isEmailError}
                        isValidInputField={isValidEmailError}
                        renderAlert={this._renderAlert}
                        onChange={this.onChange}
                    />
                );
        }
    };

    /**
     * _validation for additional fields.
     * @returns - Returns IOrderLookupAdditionalFields[].
     */
    private readonly _additionalFieldValidation = (): IOrderLookupAdditionalFields[] => {
        const additionalPropertyStates: IOrderLookupAdditionalFields[] = [];
        for (const item of this.state.additionalProperties) {
            const isFieldValid: boolean = !StringExtensions.isNullOrWhitespace(item.fieldValue) && isAdditionalFieldValid(item.fieldValue);
            additionalPropertyStates.push({
                labelName: item.labelName,
                isValidFieldName: isFieldValid,
                fieldValue: item.fieldValue,
                ariaLabel: item.ariaLabel,
                additionalfieldErrorMessage: item.additionalfieldErrorMessage
            });
        }
        return additionalPropertyStates;
    };

    /**
     * _validateOrderLookupForm submit.
     * @returns - Returns boolean.
     */
    private readonly _validateOrderLookupForm = (): boolean => {
        const { config } = this.props;
        const { orderNumber, email } = this.state;
        const { orderStatusLookUpParametersType } = config;
        let additionalPropertyStates: IOrderLookupAdditionalFields[] = [];
        let isValid = false;
        let isDisable = false;

        if (ArrayExtensions.hasElements(this.state.additionalProperties)) {
            additionalPropertyStates = this._additionalFieldValidation();
            for (const item of additionalPropertyStates) {
                if (!item.isValidFieldName) {
                    isDisable = true;
                }
            }

            if (isDisable) {
                this.setState({
                    additionalProperties: additionalPropertyStates,
                    isDisabled: true
                });
            }
        }

        if (StringExtensions.isNullOrWhitespace(orderNumber)) {
            this.setState({
                isOrdNumberError: true,
                isReqError: true,
                isDisabled: true
            });
        } else if (orderNumber.trim() !== orderNumber) {
            this.setState({
                isOrdNumberError: true,
                isReqError: false,
                isDisabled: true
            });
        } else if (orderStatusLookUpParametersType === orderStatusLookUpParametersTypes.email && StringExtensions.isNullOrWhitespace(email)) {
            this.setState({
                isEmailError: true,
                isReqError: true,
                isDisabled: true
            });
        } else if (orderStatusLookUpParametersType === orderStatusLookUpParametersTypes.email && !isEmailValid(email)) {
            this.setState({
                isValidEmailError: true,
                isDisabled: true
            });
        } else if (isDisable || !this.state.isValidLookupField || this.state.isValidAdditionalLookupField) {
            this.setState({
                isDisabled: true
            });
        } else {
            this.setState({
                isDisabled: false
            });
            isValid = true;
        }
        return isValid;
    };
}

export default OrderLookup;
